import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@utils/api";


interface PaymentException {
  _id: string;
  provider: string;
  reference?: string;
  provider_amount?: string;
  provider_currency?: string;
  authenticated?: boolean;
  section?: string;
  section_id?: string;
  attempt_id?: string;
  review_reason?: string;
  received_at?: string;
  review_appointment_id?: string;
  review_note?: string;
}

interface PaymentAttempt {
  id: string;
  provider?: string;
  status?: string;
  reference?: string;
  amount?: number;
  currency?: string;
  created_at?: string;
  processed_at?: string;
  settlement_review_reason?: string;
  recorded_failed_charges: number;
  last_failed_charge_reason?: string;
  last_failed_charge_at?: string;
}

interface ExceptionList {
  attempts?: PaymentAttempt[];
  related: PaymentException[];
  queue: PaymentException[];
}

interface Props {
  opened: boolean;
  close: () => void;
  appointmentId: string;
}

export const PaymentExceptionsModal = ({ opened, close, appointmentId }: Props) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [actionError, setActionError] = useState("");

  const queryKey = ["payment-exceptions", appointmentId, search];
  const { data, isLoading, isError } = useQuery<ExceptionList>({
    queryKey,
    queryFn: async () => (await api.get("payment-exceptions", {
      params: { appointment_id: appointmentId, search },
    })).data,
    enabled: opened,
    staleTime: 0,
  });

  const linkMutation = useMutation({
    mutationFn: async () => api.post(`payment-exceptions/${selectedId}/link`, {
      appointment_id: appointmentId,
      note: note.trim(),
    }),
    onSuccess: async () => {
      setSelectedId(null);
      setNote("");
      setActionError("");
      await queryClient.invalidateQueries({ queryKey: ["payment-exceptions", appointmentId] });
    },
    onError: (error: any) => {
      setActionError(error?.response?.data?.detail || t("payment-exception-link-error"));
    },
  });

  const receiptCard = (receipt: PaymentException, canLink: boolean) => (
    <Card key={receipt._id} withBorder radius="md" p="md">
      <Stack gap={5}>
        <Group justify="space-between" align="start">
          <Text fw={600}>{receipt.provider}</Text>
          <Badge color={receipt.authenticated ? "teal" : "orange"} variant="light">
            {receipt.authenticated ? t("payment-exception-verified") : t("payment-exception-unverified")}
          </Badge>
        </Group>
        <Text size="sm">{t("payment-exception-raw-amount")}: {receipt.provider_amount || "—"} {receipt.provider_currency || ""}</Text>
        <Text size="sm">{t("payment-exception-reason")}: {receipt.review_reason || "—"}</Text>
        <Text size="sm">{t("payment-exception-reference")}: {receipt.reference || "—"}</Text>
        <Text size="sm">{t("payment-exception-target")}: {receipt.section || "—"} / {receipt.section_id || "—"}</Text>
        <Text size="xs" c="dimmed">{t("payment-exception-attempt")}: {receipt.attempt_id || "—"}</Text>
        <Text size="xs" c="dimmed">{t("payment-exception-receipt")}: {receipt._id}</Text>
        {receipt.received_at && (
          <Text size="xs" c="dimmed">{new Date(receipt.received_at).toLocaleString(i18n.language)}</Text>
        )}
        {receipt.review_note && <Text size="sm">{t("payment-exception-note")}: {receipt.review_note}</Text>}
        {canLink && (
          <Button
            size="xs"
            variant="light"
            w="fit-content"
            onClick={() => { setSelectedId(receipt._id); setActionError(""); }}>
            {t("payment-exception-associate")}
          </Button>
        )}
      </Stack>
    </Card>
  );

  return (
    <Modal opened={opened} onClose={close} title={t("payment-activity")} size="lg" centered>
      <Stack gap="lg">
        {isLoading && <Loader size="sm" />}
        {isError && <Alert color="red">{t("payment-exception-load-error")}</Alert>}
        {data && (
          <>
            <Stack gap="xs">
              <Text fw={600}>{t("payment-activity-attempts")} ({data.attempts?.length || 0})</Text>
              {data.attempts?.length ? data.attempts.map(attempt => (
                <Card key={attempt.id} withBorder radius="md" p="md">
                  <Stack gap={5}>
                    <Group justify="space-between" align="start">
                      <Text fw={600}>{attempt.provider || "—"}</Text>
                      <Badge color={attempt.processed_at ? "teal" : attempt.settlement_review_reason ? "orange" : attempt.status === "failed" ? "red" : "blue"} variant="light">
                        {attempt.processed_at ? t("payment-activity-applied") : attempt.settlement_review_reason ? t("payment-activity-review") : (attempt.status || "—").replaceAll("_", " ")}
                      </Badge>
                    </Group>
                    {attempt.amount != null && <Text size="sm">{t("payment-activity-amount")}: {attempt.amount} {attempt.currency || ""}</Text>}
                    <Text size="sm">{t("payment-exception-reference")}: {attempt.reference || "—"}</Text>
                    {attempt.provider === "joonapay" && (
                      <Text size="sm">{t("payment-activity-failed-charges")}: {attempt.recorded_failed_charges}</Text>
                    )}
                    {attempt.last_failed_charge_reason && (
                      <Text size="sm">{t("payment-activity-last-failure")}: {attempt.last_failed_charge_reason}</Text>
                    )}
                    {attempt.last_failed_charge_at && (
                      <Text size="xs" c="dimmed">{t("payment-activity-last-failure-at")}: {new Date(attempt.last_failed_charge_at).toLocaleString(i18n.language)}</Text>
                    )}
                    {attempt.settlement_review_reason && (
                      <Text size="sm">{t("payment-exception-reason")}: {attempt.settlement_review_reason}</Text>
                    )}
                    <Text size="xs" c="dimmed">{t("payment-exception-attempt")}: {attempt.id}</Text>
                    {attempt.created_at && <Text size="xs" c="dimmed">{t("payment-activity-created")}: {new Date(attempt.created_at).toLocaleString(i18n.language)}</Text>}
                  </Stack>
                </Card>
              )) : <Text size="sm" c="dimmed">{t("payment-activity-none")}</Text>}
              <Text size="xs" c="dimmed">{t("payment-activity-history-note")}</Text>
            </Stack>
            <Alert color="blue" variant="light">{t("payment-exception-review-only")}</Alert>
            <Stack gap="xs">
              <Text fw={600}>{t("payment-exception-related")} ({data.related.length})</Text>
              {data.related.length ? data.related.map(item => receiptCard(item, !item.review_appointment_id)) : (
                <Text size="sm" c="dimmed">{t("payment-exception-none-related")}</Text>
              )}
            </Stack>
            <Stack gap="xs">
              <Text fw={600}>{t("payment-exception-queue")}</Text>
              <Group align="end">
                <TextInput
                  flex={1}
                  label={t("payment-exception-search")}
                  value={searchInput}
                  onChange={event => setSearchInput(event.currentTarget.value)}
                  placeholder={t("payment-exception-search-placeholder")}
                />
                <Button variant="light" disabled={!!searchInput.trim() && searchInput.trim().length < 3}
                  onClick={() => setSearch(searchInput.trim())}>
                  {t("search")}
                </Button>
              </Group>
              {data.queue.length ? data.queue.map(item => receiptCard(item, true)) : (
                <Text size="sm" c="dimmed">{t("payment-exception-none-queued")}</Text>
              )}
            </Stack>
          </>
        )}
        {selectedId && (
          <Card withBorder radius="md" p="md">
            <Stack gap="sm">
              <Text fw={600}>{t("payment-exception-associate")}</Text>
              <Text size="sm" c="dimmed">{selectedId}</Text>
              <Textarea
                label={t("payment-exception-note")}
                description={t("payment-exception-note-help")}
                value={note}
                onChange={event => setNote(event.currentTarget.value)}
                minRows={2}
                maxLength={1000}
              />
              {actionError && <Alert color="red">{actionError}</Alert>}
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setSelectedId(null)}>{t("cancel")}</Button>
                <Button disabled={note.trim().length < 10} loading={linkMutation.isLoading}
                  onClick={() => linkMutation.mutate()}>
                  {t("payment-exception-associate")}
                </Button>
              </Group>
            </Stack>
          </Card>
        )}
      </Stack>
    </Modal>
  );
};
